import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { calendarAPI, kioskAPI, teacherAPI, teksAPI } from "../services/api";
import {
  parseObjectiveTextToStructured,
  publishKioskPayload,
  validateStructuredObjectives,
} from "../utils/kiosk";
import "./KioskControlPage.css";

const plannerStorageKey = (courseId, weekStart) =>
  `weekly-planner:${courseId || "none"}:${weekStart || "none"}`;

const fromDateKey = (dateKey) => {
  if (!dateKey) return null;
  const parsed = new Date(`${dateKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const addDays = (dateKey, days) => {
  const parsed = fromDateKey(dateKey);
  if (!parsed) return null;
  parsed.setDate(parsed.getDate() + days);
  return parsed.toISOString().split("T")[0];
};

const mondayOfWeek = (date = new Date()) => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  const day = copy.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  copy.setDate(copy.getDate() + offset);
  return copy.toISOString().split("T")[0];
};

const toDateKey = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  if (raw.includes("T")) return raw.split("T")[0];
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString().split("T")[0];
};

const formatDate = (dateKey) => {
  const parsed = fromDateKey(dateKey);
  if (!parsed) return "TBD";
  return parsed.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
};

const defaultEntry = () => ({
  teksCode: "",
  statement: "",
  imageUrl: "",
  iCanStatements: [""],
  isSelected: true,
});
const OBJECTIVE_SELECTION_PREFIX = "objective:";
const DEFAULT_ROTATION_SECONDS = 30;
const MIN_ROTATION_SECONDS = 5;
const MAX_ROTATION_SECONDS = 300;
const ROKU_POSTER_IMAGE_URL = "https://i.postimg.cc/SR3zVkHZ/channel-poster.png";
const ROKU_BETA_APP_SCREENSHOT_URL = "https://i.postimg.cc/Hkw8NHtj/beta-app-pic.png";
const ROKU_ACCESS_CODE = "9JQDPPN";

const normalizeRotationSeconds = (value) => {
  const parsed = Number(value);
  if (!Number.isInteger(parsed)) return DEFAULT_ROTATION_SECONDS;
  if (parsed < MIN_ROTATION_SECONDS || parsed > MAX_ROTATION_SECONDS) {
    return DEFAULT_ROTATION_SECONDS;
  }
  return parsed;
};

const normalizeTeksToken = (value) => String(value || "").trim().toLowerCase();

const matchesAllowedTeksToken = (rawToken, allowedTokens) => {
  const normalizedToken = normalizeTeksToken(rawToken);
  if (!normalizedToken) return false;
  if (allowedTokens.has(normalizedToken)) return true;
  if (!normalizedToken.includes("/")) return false;

  const slashParts = normalizedToken
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);
  if (slashParts.length < 2) return false;
  return slashParts.every((part) => allowedTokens.has(part));
};

const uniqueNonEmpty = (values) =>
  Array.from(
    new Set(
      (values || [])
        .map((value) => String(value || "").trim())
        .filter(Boolean),
    ),
  );

const normalizeObjectiveEntryComparable = (entry) => {
  const teksCode = String(entry?.teksCode ?? entry?.teks_code ?? "").trim();
  const statement = String(entry?.statement ?? entry?.learning_objective ?? "").trim();
  const iCanSource = Array.isArray(entry?.iCanStatements)
    ? entry.iCanStatements
    : Array.isArray(entry?.i_can_statements)
      ? entry.i_can_statements
      : [];
  const iCanStatements = iCanSource.map((line) => String(line || "").trim()).filter(Boolean);
  return { teksCode, statement, iCanStatements };
};

const objectiveEntrySetsMatch = (leftEntries, rightEntries) => {
  const encode = (entry) => {
    const normalized = normalizeObjectiveEntryComparable(entry);
    return JSON.stringify(normalized);
  };
  const left = (leftEntries || []).map(encode).sort();
  const right = (rightEntries || []).map(encode).sort();
  if (left.length !== right.length) return false;
  return left.every((value, index) => value === right[index]);
};

const buildObjectiveDateRows = (entries) => {
  const dailyGrouped = new Map();
  const weeklyGrouped = new Map();

  (entries || []).forEach((entry) => {
    const objectiveDate = toDateKey(entry?.objective_date);
    const weekStartDate = toDateKey(entry?.week_start_date);
    const weekLabel = String(entry?.week_label || "").trim();

    // If a weekly label exists, treat this as part of a weekly objective group
    // even when an objective_date is present.
    if (weekStartDate) {
      if (weekLabel || !objectiveDate) {
        const weeklyGroupKey = `${weekStartDate}|${weekLabel || "no-label"}`;
        if (!weeklyGrouped.has(weeklyGroupKey)) {
          weeklyGrouped.set(weeklyGroupKey, {
            weekStartDate,
            weekLabel,
            entries: [],
          });
        }
        weeklyGrouped.get(weeklyGroupKey).entries.push(entry);
        return;
      }
    }

    if (objectiveDate) {
      if (!dailyGrouped.has(objectiveDate)) dailyGrouped.set(objectiveDate, []);
      dailyGrouped.get(objectiveDate).push(entry);
    }
  });

  const toRow = ({
    key,
    saveDateKey,
    sortDateKey,
    titlePrefix,
    entriesInGroup,
    rowType,
    sidebarTitle,
  }) => {
    const sortedEntries = [...entriesInGroup].sort((a, b) => {
      const lessonCompare = String(a?.lesson_label || "").localeCompare(
        String(b?.lesson_label || ""),
      );
      if (lessonCompare !== 0) return lessonCompare;
      return String(a?.teks_code || "").localeCompare(String(b?.teks_code || ""));
    });
    const lessonLabels = uniqueNonEmpty(sortedEntries.map((entry) => entry?.lesson_label));
    const unitLabels = uniqueNonEmpty(sortedEntries.map((entry) => entry?.unit_label));
    const teksCodes = uniqueNonEmpty(sortedEntries.map((entry) => entry?.teks_code));
    const resolvedSidebarTitle = sidebarTitle || formatDate(saveDateKey);

    return {
      id: `objective-row-${key}`,
      _dateKey: key,
      _saveDateKey: saveDateKey,
      _sortDateKey: sortDateKey || saveDateKey,
      _rowType: rowType,
      _sidebarTitle: resolvedSidebarTitle,
      lesson_title:
        lessonLabels[0]
        || unitLabels[0]
        || titlePrefix
        || `${sortedEntries.length} objective blocks`,
      teks_code: teksCodes[0] || "",
      objective_count: sortedEntries.length,
      objective_entries: sortedEntries,
    };
  };

  const dateRows = Array.from(dailyGrouped.entries()).map(([dateKey, dateEntries]) =>
    toRow({
      key: `date:${dateKey}`,
      saveDateKey: dateKey,
      sortDateKey: dateKey,
      titlePrefix: `${dateEntries.length} objective blocks`,
      entriesInGroup: dateEntries,
      rowType: "date",
      sidebarTitle: formatDate(dateKey),
    }),
  );

  const sortedWeeklyGroups = Array.from(weeklyGrouped.entries())
    .sort(([aKey, aGroup], [bKey, bGroup]) => {
      const dateCompare = String(aGroup.weekStartDate || "").localeCompare(
        String(bGroup.weekStartDate || ""),
      );
      if (dateCompare !== 0) return dateCompare;
      const labelCompare = String(aGroup.weekLabel || "").localeCompare(
        String(bGroup.weekLabel || ""),
      );
      if (labelCompare !== 0) return labelCompare;
      return String(aKey).localeCompare(String(bKey));
    });
  const weekRows = sortedWeeklyGroups.map(([groupKey, group], weeklyIndex) => {
    // Weekly objective groups need distinct storage dates to avoid draft collisions.
    // We keep sorting/display anchored to the true week_start_date.
    const saveDateKey = addDays(group.weekStartDate, 7 + weeklyIndex) || group.weekStartDate;
    return toRow({
      key: `week:${groupKey}`,
      saveDateKey,
      sortDateKey: group.weekStartDate,
      titlePrefix: "Weekly objectives",
      entriesInGroup: group.entries,
      rowType: "week",
      sidebarTitle: group.weekLabel || `Week of ${group.weekStartDate}`,
    });
  });

  const rows = [...weekRows, ...dateRows];
  return rows.sort((a, b) => {
    const dateCompare = String(a._sortDateKey || "").localeCompare(String(b._sortDateKey || ""));
    if (dateCompare !== 0) return dateCompare;
    if (a._rowType === b._rowType) return 0;
    return a._rowType === "week" ? -1 : 1;
  });
};

const KioskControlPage = ({ courseId }) => {
  const selectionValue = String(courseId || "").trim();
  const selectedObjectiveCourseId = useMemo(
    () =>
      selectionValue.startsWith(OBJECTIVE_SELECTION_PREFIX)
        ? selectionValue.slice(OBJECTIVE_SELECTION_PREFIX.length)
        : "",
    [selectionValue],
  );
  const isObjectiveSelection = Boolean(selectedObjectiveCourseId);
  const selectedDistrictCourseId = isObjectiveSelection ? "" : selectionValue;

  const [weekStart, setWeekStart] = useState(mondayOfWeek());
  const [loading, setLoading] = useState(false);
  const [calendarRows, setCalendarRows] = useState([]);
  const [teacherObjectiveCourses, setTeacherObjectiveCourses] = useState([]);
  const [objectiveRows, setObjectiveRows] = useState([]);
  const [objectiveRowsLoading, setObjectiveRowsLoading] = useState(false);
  const [objectiveRowsError, setObjectiveRowsError] = useState("");
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [draftsByDate, setDraftsByDate] = useState({});
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [channelKey, setChannelKey] = useState("");
  const [channelError, setChannelError] = useState("");
  const [publishStatus, setPublishStatus] = useState("");
  const [lastPublishedAt, setLastPublishedAt] = useState("");
  const [rokuCopyStatus, setRokuCopyStatus] = useState("");
  const [toastNotice, setToastNotice] = useState(null);
  const [isRokuSetupOpen, setIsRokuSetupOpen] = useState(false);
  const [rotationSecondsInput, setRotationSecondsInput] = useState(
    String(DEFAULT_ROTATION_SECONDS),
  );
  const [courseTeksOptions, setCourseTeksOptions] = useState([]);
  const [courseTeksLoading, setCourseTeksLoading] = useState(false);
  const [courseTeksError, setCourseTeksError] = useState("");
  const [activeEntryTeksKey, setActiveEntryTeksKey] = useState("");
  const [entryTeksSearchInput, setEntryTeksSearchInput] = useState("");
  const [entryTeksSearchDebounced, setEntryTeksSearchDebounced] = useState("");
  const activeEntryTeksKeyRef = useRef("");

  const weekEnd = useMemo(() => addDays(weekStart, 4), [weekStart]);
  const selectedObjectiveCourse = useMemo(() => {
    if (!selectedObjectiveCourseId) return null;
    return (
      (teacherObjectiveCourses || []).find(
        (objectiveCourse) => String(objectiveCourse.id) === String(selectedObjectiveCourseId),
      ) || null
    );
  }, [selectedObjectiveCourseId, teacherObjectiveCourses]);
  const effectiveCourseId = useMemo(() => {
    if (isObjectiveSelection) {
      return String(selectedObjectiveCourse?.kiosk_course_id || "");
    }
    return String(selectedDistrictCourseId || "");
  }, [isObjectiveSelection, selectedDistrictCourseId, selectedObjectiveCourse?.kiosk_course_id]);

  const kioskDisplayUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    if (!channelKey) return `${window.location.origin}/#/kiosk`;
    return `${window.location.origin}/#/kiosk/${channelKey}`;
  }, [channelKey]);
  const lastPublishedLabel = useMemo(() => {
    if (!lastPublishedAt) return "";
    const parsed = new Date(lastPublishedAt);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toLocaleString();
  }, [lastPublishedAt]);

  useEffect(() => {
    let cancelled = false;

    const loadTeacherObjectiveCourses = async () => {
      if (!selectedObjectiveCourseId) {
        setTeacherObjectiveCourses([]);
        return;
      }

      try {
        const response = await teacherAPI.listObjectiveCourses(false);
        if (cancelled) return;
        const rows = Array.isArray(response.data) ? response.data : [];
        setTeacherObjectiveCourses(rows);
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading teacher objective courses for kiosk control:", error);
        setTeacherObjectiveCourses([]);
      }
    };

    loadTeacherObjectiveCourses();
    return () => {
      cancelled = true;
    };
  }, [selectedObjectiveCourseId]);

  useEffect(() => {
    let cancelled = false;

    const loadChannel = async () => {
      if (!effectiveCourseId) {
        setChannelKey("");
        setChannelError("");
        setLastPublishedAt("");
        setRotationSecondsInput(String(DEFAULT_ROTATION_SECONDS));
        return;
      }

      try {
        const response = await kioskAPI.getMyChannel(effectiveCourseId);
        if (cancelled) return;
        setChannelKey(String(response.data?.channelKey || ""));
        setLastPublishedAt(String(response.data?.updatedAt || ""));
        setRotationSecondsInput(
          String(normalizeRotationSeconds(response.data?.rotationSeconds)),
        );
        setChannelError("");
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading kiosk channel:", error);
        setChannelKey("");
        setLastPublishedAt("");
        setRotationSecondsInput(String(DEFAULT_ROTATION_SECONDS));
        setChannelError("Sign in to get your kiosk channel URL.");
      }
    };

    loadChannel();
    return () => {
      cancelled = true;
    };
  }, [effectiveCourseId]);

  useEffect(() => {
    if (!isRokuSetupOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setIsRokuSetupOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isRokuSetupOpen]);

  useEffect(() => {
    if (!toastNotice) return undefined;
    const timer = window.setTimeout(() => setToastNotice(null), 4200);
    return () => window.clearTimeout(timer);
  }, [toastNotice]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setEntryTeksSearchDebounced(entryTeksSearchInput);
    }, 250);
    return () => window.clearTimeout(handle);
  }, [entryTeksSearchInput]);

  useEffect(() => {
    activeEntryTeksKeyRef.current = activeEntryTeksKey;
  }, [activeEntryTeksKey]);

  useEffect(() => {
    setActiveEntryTeksKey("");
    setEntryTeksSearchInput("");
    setEntryTeksSearchDebounced("");
  }, [effectiveCourseId, selectedDateKey]);

  useEffect(() => {
    if (!effectiveCourseId) {
      setCourseTeksOptions([]);
      setCourseTeksError("");
      setCourseTeksLoading(false);
      return;
    }

    let cancelled = false;
    const loadCourseTeks = async () => {
      setCourseTeksLoading(true);
      setCourseTeksError("");
      try {
        const response = await teksAPI.getAll(effectiveCourseId);
        if (cancelled) return;
        const rows = Array.isArray(response.data) ? response.data : [];
        const options = rows
          .map((row) => ({
            code: String(row?.teks_code || "").trim(),
            description: String(row?.description || row?.teks_description || "").trim(),
          }))
          .filter((row) => row.code)
          .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
        setCourseTeksOptions(options);
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading TEKS for kiosk editor:", error);
        setCourseTeksOptions([]);
        setCourseTeksError("Could not load TEKS for this course.");
      } finally {
        if (!cancelled) setCourseTeksLoading(false);
      }
    };

    loadCourseTeks();
    return () => {
      cancelled = true;
    };
  }, [effectiveCourseId]);

  useEffect(() => {
    if (isObjectiveSelection || !selectedDistrictCourseId || !weekStart || !weekEnd) {
      setCalendarRows([]);
      setLoading(false);
      return;
    }

    const loadWeek = async () => {
      setLoading(true);
      try {
        const calendarResponse = await calendarAPI.getRange(
          selectedDistrictCourseId,
          weekStart,
          weekEnd,
        );
        const rows = (calendarResponse.data || []).map((row) => ({
          ...row,
          _dateKey: toDateKey(row.date),
        }));

        setCalendarRows(rows);
      } catch (error) {
        console.error("Error loading kiosk control week:", error);
        setCalendarRows([]);
      } finally {
        setLoading(false);
      }
    };

    loadWeek();
  }, [isObjectiveSelection, selectedDistrictCourseId, weekEnd, weekStart]);

  useEffect(() => {
    let cancelled = false;

    const loadObjectiveRows = async () => {
      if (!isObjectiveSelection || !selectedObjectiveCourse?.id || !weekStart || !weekEnd) {
        setObjectiveRows([]);
        setObjectiveRowsError("");
        return;
      }

      setObjectiveRowsLoading(true);
      setObjectiveRowsError("");
      try {
        const response = await teacherAPI.listObjectiveEntries({
          objectiveCourseId: selectedObjectiveCourse.id,
          weekStartDate: weekStart,
          limit: 500,
        });
        if (cancelled) return;
        setObjectiveRows(buildObjectiveDateRows(response.data || []));
      } catch (error) {
        if (cancelled) return;
        console.error("Error loading objective library entries for kiosk control:", error);
        setObjectiveRows([]);
        setObjectiveRowsError(
          "Could not load objective library rows for this week. Confirm your private course selection.",
        );
      } finally {
        if (!cancelled) setObjectiveRowsLoading(false);
      }
    };

    loadObjectiveRows();
    return () => {
      cancelled = true;
    };
  }, [isObjectiveSelection, selectedObjectiveCourse?.id, weekEnd, weekStart]);

  const calendarRowsByDate = useMemo(() => {
    const map = {};
    calendarRows.forEach((row) => {
      if (row._dateKey) map[row._dateKey] = row;
    });
    return map;
  }, [calendarRows]);

  const objectiveRowsByDate = useMemo(() => {
    const map = {};
    objectiveRows.forEach((row) => {
      if (row._dateKey) map[row._dateKey] = row;
    });
    return map;
  }, [objectiveRows]);

  const activeDayRows = useMemo(
    () => (isObjectiveSelection ? objectiveRows : calendarRows),
    [isObjectiveSelection, objectiveRows, calendarRows],
  );
  const activeRowsByDate = useMemo(
    () => (isObjectiveSelection ? objectiveRowsByDate : calendarRowsByDate),
    [isObjectiveSelection, objectiveRowsByDate, calendarRowsByDate],
  );
  const selectedRow = selectedDateKey ? activeRowsByDate[selectedDateKey] : null;
  const selectedSaveDateKey = useMemo(() => {
    if (!selectedDateKey) return null;
    const mappedKey = toDateKey(selectedRow?._saveDateKey);
    return mappedKey || toDateKey(selectedDateKey);
  }, [selectedDateKey, selectedRow?._saveDateKey]);
  const selectedLesson = !isObjectiveSelection && selectedRow?.lesson_id ? selectedRow : null;

  useEffect(() => {
    const dateKeys = activeDayRows.map((row) => row._dateKey).filter(Boolean);
    if (dateKeys.length > 0) {
      setSelectedDateKey((prev) => (prev && dateKeys.includes(prev) ? prev : dateKeys[0]));
      return;
    }
    setSelectedDateKey(null);
  }, [activeDayRows]);

  useEffect(() => {
    setDraftsByDate({});
  }, [effectiveCourseId, isObjectiveSelection, selectedObjectiveCourse?.id, weekStart]);

  const createCalendarDraftForDate = useCallback(
    (dateKey) => {
      const row = calendarRowsByDate[dateKey];
      if (!row) return null;
      const persisted = localStorage.getItem(
        plannerStorageKey(selectedDistrictCourseId, weekStart),
      );
      let objectiveText = "";
      if (persisted) {
        try {
          const parsed = JSON.parse(persisted);
          objectiveText = parsed?.objectivesByDate?.[dateKey] || "";
        } catch (_error) {
          objectiveText = "";
        }
      }

      const structuredObjectives = parseObjectiveTextToStructured(
        objectiveText,
        row.teks_code || "",
      );
      const entries = structuredObjectives.map((objective) => ({
        teksCode: String(objective?.teksCode || row.teks_code || "").trim(),
        statement:
          String(objective?.statement || "").trim()
          || "The student will demonstrate mastery of the targeted standard.",
        imageUrl: "",
        iCanStatements:
          Array.isArray(objective?.iCanStatements) && objective.iCanStatements.length > 0
            ? objective.iCanStatements.map((line) => String(line || "").trim()).filter(Boolean)
            : [""],
        isSelected: true,
      }));

      return {
        displayTitle: `${row?.lesson_title || "Lesson"} - ${formatDate(dateKey)}`,
        dateKey,
        entries: entries.length > 0 ? entries : [defaultEntry()],
      };
    },
    [calendarRowsByDate, selectedDistrictCourseId, weekStart],
  );

  const createObjectiveDraftForDate = useCallback(
    (dateKey) => {
      const row = objectiveRowsByDate[dateKey];
      if (!row) return null;
      const draftDateKey = toDateKey(row._saveDateKey) || toDateKey(dateKey);
      const displayDateLabel = row._sidebarTitle || formatDate(draftDateKey);
      const objectiveEntries = Array.isArray(row.objective_entries) ? row.objective_entries : [];
      const entries = objectiveEntries.map((entry) => ({
        teksCode: String(entry?.teks_code || "").trim(),
        statement: String(entry?.learning_objective || "").trim(),
        imageUrl: "",
        iCanStatements:
          Array.isArray(entry?.i_can_statements) && entry.i_can_statements.length > 0
            ? entry.i_can_statements.map((line) => String(line || "").trim()).filter(Boolean)
            : [""],
        isSelected: true,
      }));

      return {
        displayTitle: `${selectedObjectiveCourse?.title || "Objective Library"} - ${displayDateLabel}`,
        dateKey: draftDateKey,
        entries: entries.length > 0 ? entries : [defaultEntry()],
      };
    },
    [objectiveRowsByDate, selectedObjectiveCourse?.title],
  );

  useEffect(() => {
    let cancelled = false;

    const loadDraftForDay = async () => {
      if (!effectiveCourseId || !selectedDateKey || !selectedSaveDateKey || draftsByDate[selectedDateKey]) {
        return;
      }

      try {
        const response = await kioskAPI.getMyObjectivesByDate(effectiveCourseId, selectedSaveDateKey);
        const payload = response.data;
        if (cancelled) return;

        if (payload?.entries?.length) {
          if (isObjectiveSelection) {
            const sourceDraft = createObjectiveDraftForDate(selectedDateKey);
            const sourceEntries = sourceDraft?.entries || [];
            const savedEntries = Array.isArray(payload.entries) ? payload.entries : [];
            if (!objectiveEntrySetsMatch(savedEntries, sourceEntries)) {
              if (sourceDraft) {
                setDraftsByDate((prev) => ({ ...prev, [selectedDateKey]: sourceDraft }));
                return;
              }
            }
          }

          setDraftsByDate((prev) => ({
            ...prev,
            [selectedDateKey]: {
              displayTitle:
                payload.displayTitle ||
                `${selectedLesson?.lesson_title || selectedObjectiveCourse?.title || "Lesson"} - ${selectedRow?._sidebarTitle || formatDate(selectedSaveDateKey)}`,
              dateKey: selectedSaveDateKey,
              entries: payload.entries.map((entry) => ({
                teksCode: entry.teksCode || "",
                statement: entry.statement || "",
                imageUrl: entry.imageUrl || "",
                iCanStatements:
                  Array.isArray(entry.iCanStatements) && entry.iCanStatements.length
                    ? entry.iCanStatements
                    : [""],
                isSelected: true,
              })),
            },
          }));
          return;
        }
      } catch (error) {
        console.error("Error loading teacher kiosk objectives:", error);
      }

      const generated =
        isObjectiveSelection
          ? createObjectiveDraftForDate(selectedDateKey)
          : createCalendarDraftForDate(selectedDateKey);
      if (!generated || cancelled) return;
      setDraftsByDate((prev) => ({ ...prev, [selectedDateKey]: generated }));
    };

    loadDraftForDay();

    return () => {
      cancelled = true;
    };
  }, [
    createCalendarDraftForDate,
    createObjectiveDraftForDate,
    draftsByDate,
    effectiveCourseId,
    isObjectiveSelection,
    selectedDateKey,
    selectedRow?._sidebarTitle,
    selectedSaveDateKey,
    selectedLesson?.lesson_title,
    selectedObjectiveCourse?.title,
  ]);

  const activeDraft = useMemo(() => {
    if (!selectedDateKey) return null;
    return draftsByDate[selectedDateKey] || null;
  }, [draftsByDate, selectedDateKey]);
  const filteredCourseTeksOptions = useMemo(() => {
    const query = String(entryTeksSearchDebounced || "").trim().toLowerCase();
    const rows = courseTeksOptions.filter((option) => {
      if (!query) return true;
      return (
        String(option.code || "").toLowerCase().includes(query)
        || String(option.description || "").toLowerCase().includes(query)
      );
    });
    return rows.slice(0, 12);
  }, [courseTeksOptions, entryTeksSearchDebounced]);
  const activeDraftValidation = useMemo(() => {
    const rows = Array.isArray(activeDraft?.entries) ? activeDraft.entries : [];
    const structured = rows.map((entry) => ({
      teksCode: String(entry?.teksCode || "").trim(),
      statement: String(entry?.statement || "").trim(),
      iCanStatements: Array.isArray(entry?.iCanStatements)
        ? entry.iCanStatements.map((line) => String(line || "").trim()).filter(Boolean)
        : [],
    }));
    const base = validateStructuredObjectives(structured);
    if (!courseTeksOptions.length) return base;

    const allowedCodes = new Set(
      courseTeksOptions
        .map((option) => normalizeTeksToken(option.code))
        .filter(Boolean),
    );
    const courseErrors = [];
    structured.forEach((entry, index) => {
      if (!entry.teksCode) return;
      if (!matchesAllowedTeksToken(entry.teksCode, allowedCodes)) {
        courseErrors.push(`Objective Block ${index + 1}: choose a TEKS from this course list.`);
      }
    });

    return {
      ...base,
      isValid: [...base.errors, ...courseErrors].length === 0,
      errors: [...base.errors, ...courseErrors],
    };
  }, [activeDraft?.entries, courseTeksOptions]);
  const hasDraftEntries = (activeDraft?.entries?.length || 0) > 0;
  const allDraftEntriesSelected = hasDraftEntries
    && activeDraft.entries.every((entry) => entry.isSelected !== false);

  const updateDraft = (updater) => {
    if (!selectedDateKey) return;
    setDraftsByDate((prev) => {
      const current =
        prev[selectedDateKey]
        || (isObjectiveSelection
          ? createObjectiveDraftForDate(selectedDateKey)
          : createCalendarDraftForDate(selectedDateKey));
      if (!current) return prev;
      return {
        ...prev,
        [selectedDateKey]:
          typeof updater === "function" ? updater(current) : updater,
      };
    });
  };

  const updateEntry = (entryIndex, patch) => {
    updateDraft((draft) => ({
      ...draft,
      entries: draft.entries.map((entry, idx) =>
        idx === entryIndex ? { ...entry, ...patch } : entry,
      ),
    }));
  };

  const setAllEntriesSelected = (isSelected) => {
    updateDraft((draft) => ({
      ...draft,
      entries: draft.entries.map((entry) => ({ ...entry, isSelected })),
    }));
  };

  const addEntry = () => {
    updateDraft((draft) => ({
      ...draft,
      entries: [...draft.entries, defaultEntry()],
    }));
  };

  const removeEntry = (entryIndex) => {
    updateDraft((draft) => ({
      ...draft,
      entries: draft.entries.filter((_, idx) => idx !== entryIndex),
    }));
  };

  const addICan = (entryIndex) => {
    updateDraft((draft) => ({
      ...draft,
      entries: draft.entries.map((entry, idx) =>
        idx === entryIndex
          ? { ...entry, iCanStatements: [...entry.iCanStatements, ""] }
          : entry,
      ),
    }));
  };

  const updateICan = (entryIndex, iCanIndex, value) => {
    updateDraft((draft) => ({
      ...draft,
      entries: draft.entries.map((entry, idx) =>
        idx === entryIndex
          ? {
              ...entry,
              iCanStatements: entry.iCanStatements.map((line, i) =>
                i === iCanIndex ? value : line,
              ),
            }
          : entry,
      ),
    }));
  };

  const removeICan = (entryIndex, iCanIndex) => {
    updateDraft((draft) => ({
      ...draft,
      entries: draft.entries.map((entry, idx) =>
        idx === entryIndex
          ? {
              ...entry,
              iCanStatements: entry.iCanStatements.filter((_, i) => i !== iCanIndex),
            }
          : entry,
      ),
    }));
  };

  const publishToKiosk = () => {
    const publish = async () => {
      if (!activeDraft || !selectedDateKey || !selectedSaveDateKey) {
        setPublishStatus("Select a day with objective content before publishing.");
        return;
      }
      const parsedRotationSeconds = Number(rotationSecondsInput);
      if (
        !Number.isInteger(parsedRotationSeconds)
        || parsedRotationSeconds < MIN_ROTATION_SECONDS
        || parsedRotationSeconds > MAX_ROTATION_SECONDS
      ) {
        setPublishStatus(
          `Rotation seconds must be an integer between ${MIN_ROTATION_SECONDS} and ${MAX_ROTATION_SECONDS}.`,
        );
        return;
      }
      if (!activeDraftValidation.isValid) {
        setPublishStatus(
          activeDraftValidation.errors[0]
            || "Fix objective blocks before publishing.",
        );
        return;
      }

      const normalizedEntries = activeDraft.entries.map((entry) => ({
        teksCode: (entry.teksCode || "").trim(),
        statement: (entry.statement || "").trim(),
        imageUrl: (entry.imageUrl || "").trim(),
        iCanStatements: (entry.iCanStatements || [])
          .map((line) => String(line || "").trim())
          .filter(Boolean),
        isSelected: entry.isSelected !== false,
      }));
      const selectedEntries = normalizedEntries
        .filter((entry) => entry.isSelected)
        .map(({ teksCode, statement, imageUrl, iCanStatements }) => ({
          teksCode,
          statement,
          imageUrl,
          iCanStatements,
        }));
      const allEntries = normalizedEntries.map(({ teksCode, statement, imageUrl, iCanStatements }) => ({
        teksCode,
        statement,
        imageUrl,
        iCanStatements,
      }));

      if (!selectedEntries.length) {
        setPublishStatus("Select at least one objective block to publish.");
        return;
      }

      setPublishing(true);
      setPublishStatus("");
      const publishStartedAtMs = Date.now();
      try {
        await kioskAPI.saveMyObjectivesByDate({
          courseId: effectiveCourseId,
          date: selectedSaveDateKey,
          displayTitle: activeDraft.displayTitle,
          entries: allEntries,
        });

        const payload = {
          displayTitle: activeDraft.displayTitle,
          dateKey: selectedSaveDateKey,
          courseId: effectiveCourseId,
          lessonId: selectedRow?.lesson_id || null,
          entries: selectedEntries,
          rotationSeconds: parsedRotationSeconds,
        };

        const publishResponse = await kioskAPI.publishMy(payload);
        const latestChannelKey = String(publishResponse.data?.channelKey || channelKey || "");
        if (latestChannelKey) setChannelKey(latestChannelKey);
        setLastPublishedAt(String(publishResponse.data?.updatedAt || new Date().toISOString()));
        setRotationSecondsInput(
          String(normalizeRotationSeconds(publishResponse.data?.rotationSeconds)),
        );
        const publishedAt = publishResponse.data?.updatedAt
          ? new Date(publishResponse.data.updatedAt).toLocaleString()
          : "just now";

        // Keep local publish for same-browser immediate display support.
        publishKioskPayload(payload, latestChannelKey || null);

        if (latestChannelKey) {
          try {
            const publicResponse = await kioskAPI.getPublishedPublic(latestChannelKey);
            const publicUpdatedAt = publicResponse.data?.updatedAt;
            if (publicUpdatedAt) {
              const publicUpdatedAtMs = new Date(publicUpdatedAt).getTime();
              if (
                Number.isFinite(publicUpdatedAtMs)
                && publicUpdatedAtMs < publishStartedAtMs - 2_000
              ) {
                setPublishStatus(
                  `Publish response returned, but ${latestChannelKey} still shows older timestamp ${new Date(publicUpdatedAt).toLocaleString()}. Refresh and confirm channel key.`,
                );
              } else {
                setPublishStatus(
                  `Published to ${latestChannelKey}. Display now shows update time ${new Date(publicUpdatedAt).toLocaleString()}.`,
                );
              }
            } else {
              setPublishStatus(`Published to ${latestChannelKey} at ${publishedAt}.`);
            }
          } catch (_verifyError) {
            setPublishStatus(`Published to ${latestChannelKey} at ${publishedAt}.`);
          }
        } else {
          setPublishStatus(`Published at ${publishedAt}.`);
        }
      } catch (error) {
        console.error("Error publishing kiosk payload:", error);
        const apiError = String(error?.response?.data?.error || "").trim();
        setPublishStatus(
          apiError
            ? `Publish failed: ${apiError}`
            : "Publish failed. Sign in again, then retry.",
        );
      } finally {
        setPublishing(false);
      }
    };

    publish();
  };

  const saveDraft = () => {
    const save = async () => {
      if (!activeDraft || !selectedDateKey || !selectedSaveDateKey) return;
      if (!activeDraftValidation.isValid) {
        setPublishStatus(
          activeDraftValidation.errors[0]
            || "Fix objective blocks before saving.",
        );
        return;
      }

      const normalizedEntries = activeDraft.entries.map((entry) => ({
        teksCode: (entry.teksCode || "").trim(),
        statement: (entry.statement || "").trim(),
        imageUrl: (entry.imageUrl || "").trim(),
        iCanStatements: (entry.iCanStatements || [])
          .map((line) => String(line || "").trim())
          .filter(Boolean),
      }));

      setSaving(true);
      try {
        await kioskAPI.saveMyObjectivesByDate({
          courseId: effectiveCourseId,
          date: selectedSaveDateKey,
          displayTitle: activeDraft.displayTitle,
          entries: normalizedEntries,
        });
      } catch (error) {
        console.error("Error saving kiosk objectives:", error);
      } finally {
        setSaving(false);
      }
    };

    save();
  };

  const openExternalUrl = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openKioskDisplay = () => {
    openExternalUrl(kioskDisplayUrl);
  };

  const copyTextToClipboard = async (value, successLabel, options = {}) => {
    if (!value) return;
    const { showStatus = true, onSuccess, onFailure } = options;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = value;
        textArea.setAttribute("readonly", "");
        textArea.style.position = "absolute";
        textArea.style.left = "-9999px";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      if (showStatus) setRokuCopyStatus(`${successLabel} copied.`);
      if (onSuccess) onSuccess();
    } catch (_error) {
      if (showStatus) setRokuCopyStatus(`Copy failed. ${successLabel}: ${value}`);
      if (onFailure) onFailure();
    }
  };

  const kioskPurposeSection = (
    <section className="kiosk-purpose-card" aria-label="Kiosk control purpose and setup guidance">
      <h3>Why Kiosk Control Exists</h3>
      <p>
        Kiosk Control is designed to publish daily learning objectives to a dedicated screen so teachers can comply
        with district posting mandates in a low-friction way.
      </p>
      <p>
        Objectives are stored by course and date, so teachers can save updates and re-use objective blocks instead of
        repeating the same setup tasks every day.
      </p>
      <h4>Recommended Low-Friction Setup Options</h4>
      <ul>
        <li>
          Use an existing classroom computer or Chromebook:
          set the startup page to <code>/#/kiosk/&lt;your-key&gt;</code> and keep it fullscreen on a second
          monitor or TV. Your key is your email before <code>@episd.org</code> (example:
          <code>dhohnhol@episd.org</code> -&gt; <code>dhohnhol</code>).
        </li>
        <li>
          Use a Smart TV browser app:
          bookmark the kiosk URL and pin it as the default input/home screen.
        </li>
        <li>
          Use district-managed signage:
          deploy your personal channel URL (for example <code>https://episd.guru/#/kiosk/abc123...</code>) through
          ChromeOS or MDM startup policies.
        </li>
        <li>
          Raspberry Pi remains a strong option for dedicated rooms, but the options above are usually easier for
          individual teachers to adopt.
        </li>
      </ul>
    </section>
  );

  if (!courseId) {
    return (
      <div className="panel-card kiosk-control-page">
        <h2>Kiosk Control</h2>
        <p>Select a course to configure kiosk objectives.</p>
        {kioskPurposeSection}
      </div>
    );
  }

  return (
    <div className="panel-card kiosk-control-page" data-tour="kiosk-control-root">
      <div className="kiosk-control-header" data-tour="kiosk-control-header">
        <div className="kiosk-topbar-row">
          <div className="kiosk-topbar-title">
            <h2>Kiosk Control</h2>
            <p className="kiosk-topbar-sub">Choose day, refine objectives, publish.</p>
          </div>
          <div className="kiosk-topbar-actions">
            <div className="kiosk-rotation-inline">
              <label
                className="field-label kiosk-hint-label"
                htmlFor="kiosk-rotation-seconds"
                title="When you publish multiple objective blocks, the kiosk advances to the next block after this many seconds."
              >
                Rotation
              </label>
              <input
                id="kiosk-rotation-seconds"
                type="number"
                min={MIN_ROTATION_SECONDS}
                max={MAX_ROTATION_SECONDS}
                step="1"
                value={rotationSecondsInput}
                onChange={(event) => setRotationSecondsInput(event.target.value)}
              />
              <span className="kiosk-range-pill">{MIN_ROTATION_SECONDS}-{MAX_ROTATION_SECONDS}s</span>
            </div>
            <button
              type="button"
              className="primary-btn"
              onClick={saveDraft}
              disabled={saving || publishing || !activeDraftValidation.isValid}
              title={!activeDraftValidation.isValid ? activeDraftValidation.errors[0] : undefined}
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={publishToKiosk}
              disabled={publishing || !activeDraftValidation.isValid}
              title={!activeDraftValidation.isValid ? activeDraftValidation.errors[0] : undefined}
            >
              {publishing ? "Publishing..." : "Publish"}
            </button>
            <button type="button" className="nav-lite-btn" onClick={openKioskDisplay} disabled={!channelKey}>
              Open Display
            </button>
            <button
              type="button"
              className="nav-lite-btn"
              onClick={() =>
                copyTextToClipboard(kioskDisplayUrl, "Display URL", {
                  showStatus: false,
                  onSuccess: () =>
                    setToastNotice({
                      tone: "success",
                      title: "Display link copied",
                      message:
                        "Use this link to point your dedicated browser to your personal display page.",
                    }),
                  onFailure: () =>
                    setToastNotice({
                      tone: "error",
                      title: "Copy failed",
                      message: "Could not copy the display link. Copy it manually from Open Display.",
                    }),
                })
              }
              disabled={!channelKey}
            >
              Copy Display Link
            </button>
            <button
              type="button"
              className="nav-lite-btn"
              onClick={() => setIsRokuSetupOpen(true)}
              disabled={!channelKey}
            >
              Roku Setup
            </button>
          </div>
        </div>
        <div className="kiosk-status-stack" aria-live="polite">
          {lastPublishedLabel ? (
            <p className="muted">
              Last published: <strong>{lastPublishedLabel}</strong>
            </p>
          ) : null}
          {channelError ? <p className="muted">{channelError}</p> : null}
          {publishStatus ? <p className="muted">{publishStatus}</p> : null}
          {rokuCopyStatus ? <p className="muted">{rokuCopyStatus}</p> : null}
        </div>
      </div>

      <div className="planner-week-nav" data-tour="kiosk-control-week-nav">
        <button type="button" className="nav-lite-btn" onClick={() => setWeekStart(addDays(weekStart, -7))}>
          Previous Week
        </button>
        <button type="button" className="nav-lite-btn" onClick={() => setWeekStart(mondayOfWeek())}>
          This Week
        </button>
        <button type="button" className="nav-lite-btn" onClick={() => setWeekStart(addDays(weekStart, 7))}>
          Next Week
        </button>
        <div className="week-label">
          <strong>{weekStart}</strong> to <strong>{weekEnd}</strong>
        </div>
      </div>

      {loading && <p>Loading kiosk control data...</p>}
      {!loading && isObjectiveSelection && objectiveRowsLoading ? (
        <p>Loading objective library entries for this week...</p>
      ) : null}

      {!loading && !(isObjectiveSelection && objectiveRowsLoading) && (
        <div className="kiosk-control-grid">
          <aside className="kiosk-day-list" data-tour="kiosk-control-day-list">
            <h3>{isObjectiveSelection ? "Objective Dates / Weeks" : "Week Days"}</h3>
            {!isObjectiveSelection && activeDayRows.length === 0 ? (
              <p className="muted">No calendar rows in this window.</p>
            ) : null}
            {isObjectiveSelection && activeDayRows.length === 0 ? (
              <p className="muted">
                {objectiveRowsError || "No private objective entries found for this week."}
              </p>
            ) : null}
            {activeDayRows.map((row) => (
              <button
                key={row.id || row._dateKey}
                type="button"
                className={`kiosk-day-btn ${selectedDateKey === row._dateKey ? "active" : ""}`}
                onClick={() => setSelectedDateKey(row._dateKey)}
              >
                <strong>
                  {isObjectiveSelection
                    ? row._sidebarTitle || formatDate(row._saveDateKey || row._dateKey)
                    : formatDate(row._dateKey)}
                </strong>
                <span>
                  {isObjectiveSelection
                    ? row._rowType === "week"
                      ? "Weekly objectives"
                      : row.lesson_title || "Objective entries"
                    : row.lesson_title || "No lesson title"}
                </span>
                <small>
                  {isObjectiveSelection
                    ? row.teks_code || `${Number(row.objective_count || 0)} blocks`
                    : row.teks_code || "No TEKS"}
                </small>
              </button>
            ))}
          </aside>

          <section className="kiosk-editor" data-tour="kiosk-control-editor">
            {!activeDraft && <p>Select a day to begin.</p>}

            {activeDraft && (
              <>
                <div className="kiosk-display-row">
                  <div className="field-group kiosk-display-title-field">
                    <label className="field-label" htmlFor="kiosk-display-title">
                      Display Title
                    </label>
                    <input
                      id="kiosk-display-title"
                      type="text"
                      value={activeDraft.displayTitle}
                      onChange={(e) => updateDraft((draft) => ({ ...draft, displayTitle: e.target.value }))}
                    />
                  </div>
                  {hasDraftEntries ? (
                    <label className="kiosk-publish-toggle kiosk-publish-toggle-all">
                      <input
                        type="checkbox"
                        checked={allDraftEntriesSelected}
                        onChange={(event) => setAllEntriesSelected(event.target.checked)}
                      />
                      Include all in publish
                    </label>
                  ) : null}
                </div>

                {selectedLesson?.lesson_title && (
                  <p className="muted">
                    Source Lesson: <strong>{selectedLesson.lesson_title}</strong>
                  </p>
                )}
                {isObjectiveSelection && selectedObjectiveCourse ? (
                  <p className="muted">
                    Source Objective Course: <strong>{selectedObjectiveCourse.title}</strong>
                  </p>
                ) : null}

                <div className="kiosk-entries" data-tour="kiosk-control-entries">
                  {activeDraft.entries.map((entry, entryIndex) => (
                    <article key={`entry-${entryIndex}`} className="kiosk-entry-card">
                      <div className="entry-head">
                        <h4>Objective Block {entryIndex + 1}</h4>
                        <div className="entry-head-actions">
                          <label className="kiosk-publish-toggle">
                            <input
                              type="checkbox"
                              checked={entry.isSelected !== false}
                              onChange={(event) =>
                                updateEntry(entryIndex, { isSelected: event.target.checked })
                              }
                            />
                            Include in publish
                          </label>
                          {activeDraft.entries.length > 1 && (
                            <button
                              type="button"
                              className="nav-lite-btn"
                              onClick={() => removeEntry(entryIndex)}
                            >
                              Remove Block
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="field-group">
                        <label className="field-label" htmlFor={`teks-${entryIndex}`}>
                          TEKS Code
                        </label>
                        {(() => {
                          const entryTeksPickerKey = `${selectedDateKey || "none"}:${entryIndex}`;
                          const isEntryPickerActive = activeEntryTeksKey === entryTeksPickerKey;
                          const entryTeksInputValue = isEntryPickerActive
                            ? entryTeksSearchInput
                            : String(entry.teksCode || "");
                          const hasCourseTeksResults =
                            !courseTeksLoading && filteredCourseTeksOptions.length > 0;

                          return (
                            <div className="kiosk-entry-teks-picker">
                              <input
                                id={`teks-${entryIndex}`}
                                type="search"
                                autoComplete="off"
                                value={entryTeksInputValue}
                                placeholder={courseTeksLoading ? "Loading TEKS..." : "Search course TEKS"}
                                onFocus={(event) => {
                                  setActiveEntryTeksKey(entryTeksPickerKey);
                                  setEntryTeksSearchInput(String(entry.teksCode || ""));
                                  window.requestAnimationFrame(() => {
                                    event.target.select();
                                  });
                                }}
                                onChange={(event) => {
                                  const nextValue = String(event.target.value || "");
                                  setActiveEntryTeksKey(entryTeksPickerKey);
                                  setEntryTeksSearchInput(nextValue);
                                  const exactMatch = courseTeksOptions.find(
                                    (option) =>
                                      String(option.code || "").toLowerCase()
                                      === nextValue.trim().toLowerCase(),
                                  );
                                  if (!exactMatch) return;
                                  updateEntry(entryIndex, { teksCode: exactMatch.code });
                                }}
                                onKeyDown={(event) => {
                                  if (event.key !== "Enter") return;
                                  if (!hasCourseTeksResults) return;
                                  event.preventDefault();
                                  const firstMatch = filteredCourseTeksOptions[0];
                                  updateEntry(entryIndex, { teksCode: firstMatch.code });
                                  setEntryTeksSearchInput(firstMatch.code);
                                  setActiveEntryTeksKey("");
                                }}
                                onBlur={() => {
                                  window.setTimeout(() => {
                                    if (activeEntryTeksKeyRef.current !== entryTeksPickerKey) return;
                                    setActiveEntryTeksKey("");
                                    setEntryTeksSearchInput("");
                                  }, 120);
                                }}
                              />
                              {isEntryPickerActive && (
                                <div className="kiosk-entry-teks-menu" role="listbox">
                                  {courseTeksLoading && (
                                    <div className="kiosk-entry-teks-empty">Loading TEKS...</div>
                                  )}
                                  {!courseTeksLoading && hasCourseTeksResults && (
                                    filteredCourseTeksOptions.map((option) => (
                                      <button
                                        key={option.code}
                                        type="button"
                                        className="kiosk-entry-teks-option"
                                        onMouseDown={(event) => {
                                          event.preventDefault();
                                          updateEntry(entryIndex, { teksCode: option.code });
                                          setEntryTeksSearchInput(option.code);
                                          setActiveEntryTeksKey("");
                                        }}
                                      >
                                        <strong>{option.code}</strong>
                                        {option.description ? <span>{option.description}</span> : null}
                                      </button>
                                    ))
                                  )}
                                  {!courseTeksLoading && !hasCourseTeksResults && (
                                    <div className="kiosk-entry-teks-empty">
                                      {courseTeksError || "No TEKS match for that search."}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>

                      <div className="field-group">
                        <label className="field-label" htmlFor={`statement-${entryIndex}`}>
                          Learning Objective Statement
                        </label>
                        <textarea
                          id={`statement-${entryIndex}`}
                          rows={2}
                          value={entry.statement}
                          onChange={(e) => updateEntry(entryIndex, { statement: e.target.value })}
                          placeholder="The student will..."
                        />
                      </div>

                      <div className="field-group">
                        <label className="field-label" htmlFor={`image-url-${entryIndex}`}>
                          Objective Image URL (Optional)
                        </label>
                        <input
                          id={`image-url-${entryIndex}`}
                          type="url"
                          value={entry.imageUrl || ""}
                          onChange={(e) => updateEntry(entryIndex, { imageUrl: e.target.value })}
                          placeholder="https://..."
                        />
                        {entry.imageUrl ? (
                          <img
                            className="kiosk-image-preview"
                            src={entry.imageUrl}
                            alt="Objective preview"
                            onError={(e) => {
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : null}
                      </div>

                      <div className="field-group">
                        <label className="field-label">I can Statements</label>
                        <div className="i-can-list">
                          {entry.iCanStatements.map((line, iCanIndex) => (
                            <div key={`ican-${entryIndex}-${iCanIndex}`} className="i-can-row">
                              <input
                                type="text"
                                value={line}
                                onChange={(e) =>
                                  updateICan(entryIndex, iCanIndex, e.target.value)
                                }
                                placeholder="I can ..."
                              />
                              <button
                                type="button"
                                className="nav-lite-btn"
                                onClick={() => removeICan(entryIndex, iCanIndex)}
                                disabled={entry.iCanStatements.length <= 1}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="nav-lite-btn"
                          onClick={() => addICan(entryIndex)}
                        >
                          Add I can Statement
                        </button>
                      </div>
                    </article>
                  ))}
                </div>

                {courseTeksError ? <p className="muted">{courseTeksError}</p> : null}
                {!courseTeksLoading && !courseTeksError && courseTeksOptions.length === 0 ? (
                  <p className="muted">
                    No TEKS found for this course yet. Add TEKS in Curriculum Hub first.
                  </p>
                ) : null}
                {activeDraftValidation.errors.map((message, index) => (
                  <p key={`kiosk-objective-error-${index}`} className="kiosk-objective-guard-error">
                    {message}
                  </p>
                ))}
                {activeDraftValidation.warnings.map((message, index) => (
                  <p key={`kiosk-objective-warning-${index}`} className="kiosk-objective-guard-warning">
                    {message}
                  </p>
                ))}

                <button type="button" className="nav-lite-btn" onClick={addEntry}>
                  Add Objective Block
                </button>
              </>
            )}
          </section>
        </div>
      )}

      {isRokuSetupOpen ? (
        <div
          className="kiosk-roku-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Roku setup instructions"
          onClick={() => setIsRokuSetupOpen(false)}
        >
          <div className="kiosk-roku-modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="kiosk-roku-modal-header">
              <h3>Roku Setup</h3>
              <button type="button" className="nav-lite-btn" onClick={() => setIsRokuSetupOpen(false)}>
                Close
              </button>
            </div>
            <div className="kiosk-roku-modal-body">
              <img
                src={ROKU_POSTER_IMAGE_URL}
                alt="Kiosk App poster for Roku"
                className="kiosk-roku-poster"
                loading="lazy"
              />
              <div className="kiosk-roku-copy">
                <p className="muted">
                  Add the app from your Roku account first. When you open the app on your Roku, enter your key (the
                  part of your email before the @ symbol).
                </p>
                <p className="muted">Beta app code:</p>
                <div className="kiosk-key-row">
                  <code className="kiosk-key-pill">{ROKU_ACCESS_CODE}</code>
                  <button
                    type="button"
                    className="nav-lite-btn"
                    onClick={() => copyTextToClipboard(ROKU_ACCESS_CODE, "Roku beta code")}
                  >
                    Copy Beta Code
                  </button>
                </div>
                <p className="muted">Pairing key (email stem):</p>
                <div className="kiosk-key-row">
                  <code className="kiosk-key-pill">{channelKey}</code>
                  <button
                    type="button"
                    className="nav-lite-btn"
                    onClick={() => copyTextToClipboard(channelKey, "Roku key")}
                  >
                    Copy Key
                  </button>
                </div>
                <ol className="kiosk-roku-steps">
                  <li>Go to my.roku.com and sign in with the same email used on your Roku device.</li>
                  <li>Scroll down the left sidebar to the Streaming Store section, then select Add Beta App with Code.</li>
                  <li>Paste the beta code copied from this page ({ROKU_ACCESS_CODE}) and submit.</li>
                  <li>Wait about 24 hours for the app to appear on your Roku home screen.</li>
                  <li>Open the kiosk app. When it starts, enter your key to pair this site to the app (your key is your email stem).</li>
                  <li>Publish from this page whenever objectives change.</li>
                </ol>
                <p className="muted">Reference screenshot (Streaming Store - Add Beta App with Code):</p>
                <a href={ROKU_BETA_APP_SCREENSHOT_URL} target="_blank" rel="noopener noreferrer">
                  <img
                    src={ROKU_BETA_APP_SCREENSHOT_URL}
                    alt="Roku account sidebar showing Streaming Store and Add Beta App with Code"
                    className="kiosk-roku-reference-image"
                    loading="lazy"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {toastNotice ? (
        <div
          className={`kiosk-toast ${toastNotice.tone === "error" ? "error" : "success"}`}
          role="status"
          aria-live="polite"
        >
          <p className="kiosk-toast-title">{toastNotice.title}</p>
          <p className="kiosk-toast-message">{toastNotice.message}</p>
        </div>
      ) : null}

      {kioskPurposeSection}
    </div>
  );
};

export default KioskControlPage;
